import { useStableCallback } from "@bezoned/shared/hooks/use-stable-callback.js";
import type { OfficePresetManifest } from "@bezoned/shared/schemas/office/office-preset-manifest.schema.js";
import type { TeamAvailableChannelsGetResponse } from "@bezoned/shared/types/api/team/team-available-channels-get.types.js";
import type {
  TeamOfficeGetResponse,
  TeamOfficeTable,
} from "@bezoned/shared/types/api/team/team-office-get.types.js";
import type { TeamOfficePresetUpdateBody } from "@bezoned/shared/types/api/team/team-office-preset-update.types.js";
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  MessageBar,
  MessageBarBody,
} from "@fluentui/react-components";
import { useMutation } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";

import { teamOfficePresetUpdate } from "../../../../../services/api.service.js";
import { recordErrorInSentry } from "../../../../../utils/init-sentry.js";
import { getChannelAssignmentFromValue } from "./channel-assignment.helpers.js";
import type { ChannelDraftAssignment } from "./channel-assignment.helpers.js";
import { ChannelStepContent } from "./components/channel-step-content.js";
import { PairingStepContent } from "./components/pairing-step-content.js";
import {
  buildInitialRowSelections,
  buildPayload,
  DO_NOT_USE,
} from "./team-edit-theme-switch-dialog.helpers.js";
import type { CurrentTableDisplayItem } from "./team-edit-theme-switch-dialog.helpers.js";

type CurrentPresetTable = NonNullable<
  TeamOfficeGetResponse["preset"]["manifest"]["tables"]
>[number];

type NewPresetTable = OfficePresetManifest["tables"][number];

type AvailableChannel = TeamAvailableChannelsGetResponse["channels"][number];

type WizardStep = "pairing" | "channels";

interface SwitchOfficeThemeDialogProps {
  open: boolean;
  onClose: () => void;
  msTeamId: string;
  presetId: string;
  currentOfficeTables: TeamOfficeTable[];
  currentPresetTables: CurrentPresetTable[];
  newPresetTables: NewPresetTable[];
  availableChannels: AvailableChannel[];
  isChannelsLoading: boolean;
  onSave: () => void;
}

export const TeamEditThemeSwitchDialog = ({
  open,
  onClose,
  msTeamId,
  presetId,
  currentOfficeTables,
  currentPresetTables,
  newPresetTables,
  availableChannels,
  isChannelsLoading,
  onSave,
}: SwitchOfficeThemeDialogProps) => {
  const [step, setStep] = useState<WizardStep>("pairing");

  const currentTableDisplayData = useMemo((): CurrentTableDisplayItem[] => {
    const presetTableMap = new Map(
      currentPresetTables.map((pt) => [pt.id, pt])
    );

    return currentOfficeTables
      .filter((ot) => ot.msChannelId)
      .map((ot) => ({
        displayName:
          ot.name ??
          presetTableMap.get(ot.presetTableId)?.defaultName ??
          "Unknown table",
        msChannelId: ot.msChannelId as string,
      }));
  }, [currentOfficeTables, currentPresetTables]);

  const initialRowSelections = useMemo(
    () =>
      buildInitialRowSelections(
        currentTableDisplayData.length,
        newPresetTables
      ),
    [currentTableDisplayData.length, newPresetTables]
  );

  const [rowSelections, setRowSelections] = useState<Record<number, string>>(
    {}
  );
  const [channelAssignments, setChannelAssignments] = useState<
    Record<string, ChannelDraftAssignment>
  >({});

  const rowCount = Math.max(
    currentTableDisplayData.length,
    newPresetTables.length
  );
  const hasNoNewTables = newPresetTables.length === 0;

  // Lint exception: presetId resets state when a different preset is selected
  useEffect(() => {
    if (!open) {
      return;
    }

    setStep("pairing");
    setRowSelections(initialRowSelections);
    setChannelAssignments({});
  }, [open, initialRowSelections, presetId]);

  const { duplicateNewTableIds, allNewTablesAssigned } = useMemo(() => {
    const counts = new Map<string, number>();
    for (const id of Object.values(rowSelections)) {
      if (id && id !== DO_NOT_USE) {
        counts.set(id, (counts.get(id) ?? 0) + 1);
      }
    }
    const dupes = new Set<string>();
    for (const [id, count] of counts) {
      if (count > 1) {
        dupes.add(id);
      }
    }
    return {
      allNewTablesAssigned: counts.size === newPresetTables.length,
      duplicateNewTableIds: dupes,
    };
  }, [rowSelections, newPresetTables.length]);

  const hasDuplicates = duplicateNewTableIds.size > 0;

  const unpairedNewTables = useMemo(() => {
    const pairedIds = new Set<string>();

    for (const [rowIdx, newTableId] of Object.entries(rowSelections)) {
      const idx = Number(rowIdx);
      const hasCurrent = idx < currentTableDisplayData.length;
      if (hasCurrent && newTableId && newTableId !== DO_NOT_USE) {
        pairedIds.add(newTableId);
      }
    }

    return newPresetTables.filter((t) => !pairedIds.has(t.id));
  }, [rowSelections, currentTableDisplayData, newPresetTables]);

  const channelNameById = useMemo(
    () => new Map(availableChannels.map((ch) => [ch.msChannelId, ch.name])),
    [availableChannels]
  );

  const initiallyAssignedChannelIds = useMemo(() => {
    const ids = new Set<string>();
    for (const ot of currentOfficeTables) {
      if (ot.msChannelId) {
        ids.add(ot.msChannelId);
      }
    }
    return ids;
  }, [currentOfficeTables]);

  const allPairedChannelIds = useMemo(() => {
    const ids = new Set<string>();

    for (const [rowIdx, newTableId] of Object.entries(rowSelections)) {
      const idx = Number(rowIdx);
      const current = currentTableDisplayData.at(idx);
      if (current && newTableId && newTableId !== DO_NOT_USE) {
        ids.add(current.msChannelId);
      }
    }

    for (const assignment of Object.values(channelAssignments)) {
      if (assignment.mode === "existing_channel" && assignment.msChannelId) {
        ids.add(assignment.msChannelId);
      }
    }

    return ids;
  }, [rowSelections, currentTableDisplayData, channelAssignments]);

  const hasInvalidChannelAssignments = useMemo(
    () =>
      unpairedNewTables.some((table) => {
        const assignment = channelAssignments[table.id] ?? {
          mode: "create_new",
        };
        return (
          assignment.mode === "existing_channel" && !assignment.msChannelId
        );
      }),
    [unpairedNewTables, channelAssignments]
  );

  const switchMutation = useMutation({
    mutationFn: (tableAssignments: TeamOfficePresetUpdateBody["assignments"]) =>
      teamOfficePresetUpdate({
        assignments: tableAssignments,
        msTeamId,
        presetId,
      }),
    onError: (error) => {
      recordErrorInSentry(
        error instanceof Error ? error : new Error(String(error))
      );
    },
    onSuccess: () => {
      onSave();
    },
  });

  const handleSave = () => {
    if (hasNoNewTables) {
      return;
    }

    const assignments = buildPayload(
      newPresetTables,
      rowSelections,
      currentTableDisplayData,
      channelAssignments
    );
    switchMutation.mutate(assignments);
  };

  const handleNextOrSubmit = () => {
    if (hasNoNewTables) {
      return;
    }

    if (unpairedNewTables.length > 0) {
      setStep("channels");
    } else {
      handleSave();
    }
  };

  const handleClose = () => onClose();

  const handleBack = () => {
    setChannelAssignments({});
    setStep("pairing");
  };

  const handleRowSelectionChange = (rowIndex: number, value: string) => {
    setRowSelections((prev) => ({
      ...prev,
      [rowIndex]: value,
    }));
  };

  const handleChannelAssignment = (tableId: string, value: string) => {
    setChannelAssignments((prev) => ({
      ...prev,
      [tableId]: getChannelAssignmentFromValue(value),
    }));
  };
  const handleOpenChange271 = useStableCallback((_event, data) => {
    if (!data.open) {
      handleClose();
    }
  });

  return (
    <Dialog onOpenChange={handleOpenChange271} open={open}>
      <DialogSurface className="sm:min-w-[800px]">
        <DialogBody>
          <DialogTitle>
            {step === "pairing"
              ? "Switch office design"
              : "Configure new table channels"}
          </DialogTitle>
          <DialogContent className="overflow-x-hidden">
            {Boolean(switchMutation.isError) && (
              <MessageBar className="mb-4" intent="error">
                <MessageBarBody>
                  Failed to switch office design. Please try again.
                </MessageBarBody>
              </MessageBar>
            )}
            {step === "pairing" && hasNoNewTables && (
              <MessageBar className="mb-4" intent="warning">
                <MessageBarBody>
                  Selected design has no tables to assign. Please choose another
                  one.
                </MessageBarBody>
              </MessageBar>
            )}

            {step === "pairing" ? (
              <PairingStepContent
                currentTableDisplayData={currentTableDisplayData}
                duplicateNewTableIds={duplicateNewTableIds}
                initialRowSelections={initialRowSelections}
                newPresetTables={newPresetTables}
                onSelectionChange={handleRowSelectionChange}
                rowCount={rowCount}
                rowSelections={rowSelections}
              />
            ) : (
              <>
                <p className="mb-2 text-sm">
                  Your new office has more tables than the previous one. Each
                  table needs to be paired with an MS Teams channel. Please
                  select which channels you want to use.
                </p>
                <ChannelStepContent
                  allPairedChannelIds={allPairedChannelIds}
                  availableChannels={availableChannels}
                  channelAssignments={channelAssignments}
                  channelNameById={channelNameById}
                  initiallyAssignedChannelIds={initiallyAssignedChannelIds}
                  isChannelsLoading={isChannelsLoading}
                  onChannelAssignment={handleChannelAssignment}
                  unpairedNewTables={unpairedNewTables}
                />
              </>
            )}
          </DialogContent>
          <DialogActions>
            {step === "channels" && (
              <Button
                appearance="secondary"
                disabled={switchMutation.isPending}
                onClick={handleBack}
                type="button"
              >
                Back
              </Button>
            )}
            <Button
              appearance="secondary"
              disabled={switchMutation.isPending}
              onClick={handleClose}
              type="button"
            >
              Cancel
            </Button>
            {step === "pairing" ? (
              <Button
                appearance="primary"
                disabled={
                  switchMutation.isPending ||
                  hasNoNewTables ||
                  !allNewTablesAssigned ||
                  hasDuplicates
                }
                onClick={handleNextOrSubmit}
                type="button"
              >
                {unpairedNewTables.length > 0 && "Next"}
                {unpairedNewTables.length === 0 &&
                  (switchMutation.isPending ? "Saving..." : "Save")}
              </Button>
            ) : (
              <Button
                appearance="primary"
                disabled={
                  switchMutation.isPending || hasInvalidChannelAssignments
                }
                onClick={handleSave}
                type="button"
              >
                {switchMutation.isPending ? "Saving..." : "Save"}
              </Button>
            )}
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
